import { useEffect, useState } from 'react'
import { AlertCircle, Plus, X, Wrench, QrCode, Download, FileText, User } from 'lucide-react'
import api from '../../services/api'
import { useAuthStore } from '../../stores/authStore'

const INSPECTION_COLOUR: Record<string, string> = {
  overdue: 'var(--red)',
  due_soon: 'var(--amber)',
  ok: 'var(--green)',
}
const INSPECTION_BADGE: Record<string, string> = {
  overdue: 'badge-critical',
  due_soon: 'badge-high',
  ok: 'badge-low',
}
const STATUS_BADGE: Record<string, string> = {
  active: 'badge-low',
  inactive: 'badge-medium',
  under_repair: 'badge-high',
  decommissioned: 'badge-critical',
}

const FREQUENCY_OPTIONS = [
  { value: 7, label: 'Weekly' },
  { value: 30, label: 'Monthly' },
  { value: 90, label: 'Quarterly' },
  { value: 183, label: 'Bi-annual' },
  { value: 365, label: 'Annual' },
]

export default function AssetsList() {
  const { accessToken } = useAuthStore()
  const [data, setData] = useState<any[]>([])
  const [categories, setCategories] = useState<string[]>([])
  const [users, setUsers] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [filter, setFilter] = useState({ category: '', status: '', inspection_status: '', assigned_to_id: '' })
  const [showAdd, setShowAdd] = useState(false)
  const [form, setForm] = useState({
    name: '', asset_id: '', category: '', location: '', description: '',
    make: '', model: '', serial_number: '',
    inspection_frequency_days: 365, last_inspection_date: '',
    assigned_to_id: '',
  })
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [showExportMenu, setShowExportMenu] = useState(false)
  const [exporting, setExporting] = useState<string | null>(null)

  const load = async () => {
    setLoading(true)
    try {
      const params = new URLSearchParams()
      if (filter.category) params.set('category', filter.category)
      if (filter.status) params.set('status', filter.status)
      if (filter.assigned_to_id) params.set('assigned_to_id', filter.assigned_to_id)
      const { data: res } = await api.get(`/assets?${params}`)
      let rows = res.data || []
      if (filter.inspection_status) rows = rows.filter((r: any) => r.inspection_status === filter.inspection_status)
      setData(rows)
    } catch {}
    setLoading(false)
  }

  useEffect(() => {
    api.get('/assets/categories').catch(() => ({ data: [] })).then(r => setCategories(r.data?.categories || r.data || []))
    api.get('/users').catch(() => ({ data: [] })).then(r => setUsers((r.data || []).filter((u: any) => u.active)))
  }, [])

  useEffect(() => { load() }, [filter])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!form.name.trim()) { setError('Asset name is required'); return }
    setSaving(true)
    setError('')
    try {
      await api.post('/assets', {
        ...form,
        last_inspection_date: form.last_inspection_date || undefined,
        asset_id: form.asset_id || undefined,
        assigned_to_id: form.assigned_to_id || undefined,
      })
      setShowAdd(false)
      setForm({
        name: '', asset_id: '', category: '', location: '', description: '',
        make: '', model: '', serial_number: '',
        inspection_frequency_days: 365, last_inspection_date: '',
        assigned_to_id: '',
      })
      load()
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to save')
    }
    setSaving(false)
  }

  const downloadExport = async (format: 'csv' | 'xlsx') => {
    setExporting(format)
    setShowExportMenu(false)
    try {
      const params = new URLSearchParams()
      if (filter.category) params.set('category', filter.category)
      if (filter.status) params.set('status', filter.status)
      if (filter.assigned_to_id) params.set('assigned_to_id', filter.assigned_to_id)
      const resp = await fetch(`/api/assets/export/${format}?${params}`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      })
      if (!resp.ok) throw new Error()
      const blob = await resp.blob()
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `asset-register-${new Date().toISOString().slice(0, 10)}.${format}`
      a.click()
      URL.revokeObjectURL(url)
    } catch { alert('Export failed. Try again.') }
    setExporting(null)
  }

  const overdueCount = data.filter(d => d.inspection_status === 'overdue').length
  const dueSoonCount = data.filter(d => d.inspection_status === 'due_soon').length

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold" style={{ color: 'var(--text)' }}>Asset Register</h1>
          <p className="text-sm mt-0.5" style={{ color: 'var(--text-muted)' }}>{data.length} assets</p>
        </div>
        <div className="flex items-center gap-2">
          {/* Export dropdown */}
          <div style={{ position: 'relative' }}>
            <button
              onClick={() => setShowExportMenu(m => !m)}
              disabled={!!exporting}
              className="btn btn-secondary text-sm flex items-center gap-1.5"
            >
              <Download size={13} />
              {exporting ? `Exporting ${exporting.toUpperCase()}…` : 'Export'}
            </button>
            {showExportMenu && (
              <div
                style={{
                  position: 'absolute', top: '100%', right: 0, marginTop: 4,
                  background: 'var(--surface)', border: '1px solid var(--border)',
                  borderRadius: 10, boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
                  minWidth: 160, zIndex: 50,
                }}
                onMouseLeave={() => setShowExportMenu(false)}
              >
                <button
                  onClick={() => downloadExport('csv')}
                  style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, color: 'var(--text)' }}
                  onMouseEnter={e => (e.currentTarget.style.background = 'var(--bg)')}
                  onMouseLeave={e => (e.currentTarget.style.background = 'none')}
                >
                  <FileText size={14} style={{ color: 'var(--green)' }} /> CSV Spreadsheet
                </button>
                <div style={{ height: 1, background: 'var(--border)', margin: '0 8px' }} />
                <button
                  onClick={() => downloadExport('xlsx')}
                  style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, color: 'var(--text)' }}
                  onMouseEnter={e => (e.currentTarget.style.background = 'var(--bg)')}
                  onMouseLeave={e => (e.currentTarget.style.background = 'none')}
                >
                  <FileText size={14} style={{ color: 'var(--blue)' }} /> Excel Workbook
                </button>
              </div>
            )}
          </div>
          <button onClick={() => setShowAdd(true)} className="btn btn-primary"><Plus size={16} /> Add Asset</button>
        </div>
      </div>

      {overdueCount > 0 && (
        <div className="flex items-center gap-2 p-3 rounded-lg text-sm" style={{ background: 'var(--red-light)', color: 'var(--red)', border: '1px solid var(--red)' }}>
          <AlertCircle size={16} className="flex-shrink-0" />
          <strong>{overdueCount} asset{overdueCount > 1 ? 's' : ''}</strong> with overdue inspections — arrange inspections before use
        </div>
      )}
      {dueSoonCount > 0 && (
        <div className="flex items-center gap-2 p-3 rounded-lg text-sm" style={{ background: 'var(--amber-light, #fffbeb)', color: 'var(--amber)', border: '1px solid var(--amber)' }}>
          <AlertCircle size={16} className="flex-shrink-0" />
          <strong>{dueSoonCount} asset{dueSoonCount > 1 ? 's' : ''}</strong> due for inspection within 30 days
        </div>
      )}

      {/* Filters */}
      <div className="card p-4 flex flex-wrap gap-3">
        <select className="w-auto" value={filter.category} onChange={e => setFilter(f => ({ ...f, category: e.target.value }))}>
          <option value="">All categories</option>
          {categories.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        <select className="w-auto" value={filter.status} onChange={e => setFilter(f => ({ ...f, status: e.target.value }))}>
          <option value="">All statuses</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
          <option value="under_repair">Under Repair</option>
          <option value="decommissioned">Decommissioned</option>
        </select>
        <select className="w-auto" value={filter.inspection_status} onChange={e => setFilter(f => ({ ...f, inspection_status: e.target.value }))}>
          <option value="">All inspection states</option>
          <option value="overdue">Overdue</option>
          <option value="due_soon">Due Soon</option>
          <option value="ok">OK</option>
        </select>
        <select className="w-auto" value={filter.assigned_to_id} onChange={e => setFilter(f => ({ ...f, assigned_to_id: e.target.value }))}>
          <option value="">All users</option>
          <option value="__unassigned__">Unassigned</option>
          {users.map(u => <option key={u.id} value={u.id}>{u.first_name} {u.last_name}</option>)}
        </select>
      </div>

      {loading ? (
        <div className="text-center py-12" style={{ color: 'var(--text-muted)' }}>Loading...</div>
      ) : data.length === 0 ? (
        <div className="card text-center py-16">
          <Wrench size={40} className="mx-auto mb-3" style={{ color: 'var(--text-muted)' }} />
          <p className="font-medium" style={{ color: 'var(--text)' }}>No assets registered</p>
          <p className="text-sm mt-1" style={{ color: 'var(--text-muted)' }}>Track plant, equipment and assets with inspection schedules</p>
        </div>
      ) : (
        <div className="card p-0 overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr style={{ borderBottom: '1px solid var(--border)', background: 'var(--bg)' }}>
                <th className="text-left px-4 py-3 font-semibold" style={{ color: 'var(--text-secondary)' }}>Asset</th>
                <th className="text-left px-4 py-3 font-semibold" style={{ color: 'var(--text-secondary)' }}>Number</th>
                <th className="text-left px-4 py-3 font-semibold" style={{ color: 'var(--text-secondary)' }}>Category</th>
                <th className="text-left px-4 py-3 font-semibold" style={{ color: 'var(--text-secondary)' }}>Location</th>
                <th className="text-left px-4 py-3 font-semibold" style={{ color: 'var(--text-secondary)' }}>Assigned To</th>
                <th className="text-left px-4 py-3 font-semibold" style={{ color: 'var(--text-secondary)' }}>Next Inspection</th>
                <th className="text-left px-4 py-3 font-semibold" style={{ color: 'var(--text-secondary)' }}>Inspection</th>
                <th className="text-left px-4 py-3 font-semibold" style={{ color: 'var(--text-secondary)' }}>Status</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {data.map(a => (
                <tr key={a.id} style={{ borderBottom: '1px solid var(--border)' }}>
                  <td className="px-4 py-3">
                    <p className="font-medium" style={{ color: 'var(--text)' }}>{a.name}</p>
                    {a.make && <p className="text-xs" style={{ color: 'var(--text-muted)' }}>{a.make}{a.model ? ` — ${a.model}` : ''}</p>}
                  </td>
                  <td className="px-4 py-3 font-mono text-xs" style={{ color: 'var(--text-secondary)' }}>{a.asset_id || '—'}</td>
                  <td className="px-4 py-3 text-xs" style={{ color: 'var(--text-secondary)' }}>{a.category || '—'}</td>
                  <td className="px-4 py-3 text-xs" style={{ color: 'var(--text-secondary)' }}>{a.location || '—'}</td>
                  <td className="px-4 py-3 text-xs">
                    {a.assigned_to_name ? (
                      <div className="flex items-center gap-1.5">
                        <div className="w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0" style={{ background: 'var(--accent-light, #eff6ff)', color: 'var(--accent)' }}>
                          {(a.assigned_to_name || '?')[0].toUpperCase()}
                        </div>
                        <span style={{ color: 'var(--text-secondary)' }}>{a.assigned_to_name}</span>
                      </div>
                    ) : (
                      <span style={{ color: 'var(--text-muted)' }}>—</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-xs font-medium" style={{ color: INSPECTION_COLOUR[a.inspection_status] }}>
                    {a.next_inspection_date ? new Date(a.next_inspection_date).toLocaleDateString('en-AU') : '—'}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`badge ${INSPECTION_BADGE[a.inspection_status] || 'badge-medium'} capitalize`}>
                      {a.inspection_status?.replace('_', ' ') || 'not set'}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`badge ${STATUS_BADGE[a.status] || 'badge-medium'} capitalize`}>
                      {a.status?.replace('_', ' ')}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <a
                      href={`/api/assets/${a.id}/qr`}
                      target="_blank"
                      rel="noreferrer"
                      title="Download QR code"
                      className="flex items-center justify-center w-7 h-7 rounded-lg hover:bg-gray-100 transition-all"
                      style={{ color: 'var(--text-muted)' }}
                    >
                      <QrCode size={15} />
                    </a>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Add modal */}
      {showAdd && (
        <div className="fixed inset-0 z-50 flex items-start justify-center p-4 overflow-y-auto" style={{ background: 'rgba(0,0,0,0.5)' }}>
          <div className="w-full max-w-2xl rounded-2xl shadow-xl my-4" style={{ background: 'var(--white)' }}>
            <div className="flex items-center justify-between px-6 py-4 border-b sticky top-0 rounded-t-2xl" style={{ borderColor: 'var(--border)', background: 'var(--white)' }}>
              <h2 className="font-semibold text-lg" style={{ color: 'var(--text)' }}>Add Asset</h2>
              <button onClick={() => setShowAdd(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)' }}><X size={20} /></button>
            </div>
            <form onSubmit={handleSubmit} className="px-6 py-5 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <label className="text-sm font-medium mb-1 block" style={{ color: 'var(--text-secondary)' }}>Asset Name *</label>
                  <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="e.g. Forklift #3" required />
                </div>
                <div>
                  <label className="text-sm font-medium mb-1 block" style={{ color: 'var(--text-secondary)' }}>Asset ID</label>
                  <input value={form.asset_id} onChange={e => setForm(f => ({ ...f, asset_id: e.target.value }))} placeholder="e.g. AST-001 (auto if blank)" />
                </div>
                <div>
                  <label className="text-sm font-medium mb-1 block" style={{ color: 'var(--text-secondary)' }}>Category</label>
                  <input value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))} placeholder="e.g. Plant & Equipment" list="cat-list" />
                  <datalist id="cat-list">
                    {categories.map(c => <option key={c} value={c} />)}
                  </datalist>
                </div>
                <div>
                  <label className="text-sm font-medium mb-1 block" style={{ color: 'var(--text-secondary)' }}>Location</label>
                  <input value={form.location} onChange={e => setForm(f => ({ ...f, location: e.target.value }))} placeholder="e.g. Warehouse A" />
                </div>
                <div>
                  <label className="text-sm font-medium mb-1 block" style={{ color: 'var(--text-secondary)' }}>Assigned To</label>
                  <select value={form.assigned_to_id} onChange={e => setForm(f => ({ ...f, assigned_to_id: e.target.value }))}>
                    <option value="">— Unassigned —</option>
                    {users.map(u => (
                      <option key={u.id} value={u.id}>
                        {u.first_name} {u.last_name}{u.job_title ? ` (${u.job_title})` : ''}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-sm font-medium mb-1 block" style={{ color: 'var(--text-secondary)' }}>Make / Manufacturer</label>
                  <input value={form.make} onChange={e => setForm(f => ({ ...f, make: e.target.value }))} />
                </div>
                <div>
                  <label className="text-sm font-medium mb-1 block" style={{ color: 'var(--text-secondary)' }}>Model</label>
                  <input value={form.model} onChange={e => setForm(f => ({ ...f, model: e.target.value }))} />
                </div>
                <div>
                  <label className="text-sm font-medium mb-1 block" style={{ color: 'var(--text-secondary)' }}>Serial Number</label>
                  <input value={form.serial_number} onChange={e => setForm(f => ({ ...f, serial_number: e.target.value }))} />
                </div>
              </div>

              <div className="p-4 rounded-xl space-y-3" style={{ background: 'var(--bg)', border: '1px solid var(--border)' }}>
                <p className="text-sm font-semibold" style={{ color: 'var(--text)' }}>Inspection Schedule</p>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium mb-1 block" style={{ color: 'var(--text-secondary)' }}>Frequency</label>
                    <select value={form.inspection_frequency_days} onChange={e => setForm(f => ({ ...f, inspection_frequency_days: parseInt(e.target.value) }))}>
                      {FREQUENCY_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="text-sm font-medium mb-1 block" style={{ color: 'var(--text-secondary)' }}>Last Inspection Date</label>
                    <input type="date" value={form.last_inspection_date} onChange={e => setForm(f => ({ ...f, last_inspection_date: e.target.value }))} />
                  </div>
                </div>
              </div>

              <div>
                <label className="text-sm font-medium mb-1 block" style={{ color: 'var(--text-secondary)' }}>Description / Notes</label>
                <textarea rows={2} value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} placeholder="Any relevant notes..." />
              </div>

              {error && (
                <div className="flex items-start gap-2 text-sm p-3 rounded-lg" style={{ background: 'var(--red-light)', color: 'var(--red)' }}>
                  <AlertCircle size={15} className="flex-shrink-0 mt-0.5" />{error}
                </div>
              )}

              <div className="flex gap-3 pb-2">
                <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? 'Saving...' : 'Add Asset'}</button>
                <button type="button" className="btn btn-secondary" onClick={() => setShowAdd(false)}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}
